// Fee & Obligation PR-4b — §17.4 enforcement-administration time log, on the
// Defaults & Workout case view. Logs B4 time (date / hours / activity / note),
// shows the running unbilled total, and the current month's projected rollup
// amount (what the monthly worker will bill into a recoverable WorkoutCostEntry).

import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import {
  useBillWorkoutTime,
  useLogWorkoutTime,
  useWorkoutTimeActivityCodes,
  useWorkoutTimeLog,
} from "@/hooks/portfolioAdmin/useWorkoutTime";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

const inputClass =
  "h-10 px-space-2 border border-border-light rounded-md text-small";

function todayIso(tz: string): string {
  return isoDateInTz(new Date(), tz);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-caption text-gray-mid uppercase tracking-button">
        {label}
      </span>
      <span className="text-body font-bold tabular-nums">{value}</span>
    </div>
  );
}

export function TimeLogSection({
  caseId,
  loanId,
}: {
  caseId: string;
  loanId: string;
}) {
  const auth = useAuth();
  const view = useWorkoutTimeLog(caseId);
  const codes = useWorkoutTimeActivityCodes();
  const log = useLogWorkoutTime(caseId);
  const bill = useBillWorkoutTime(caseId, loanId);
  const tz = useUserTimeZone();

  const [workDate, setWorkDate] = useState(todayIso(tz));
  const [hours, setHours] = useState("");
  const [activity, setActivity] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);

  const data = view.data;
  const canLog = data?.canLog ?? false;
  const activityCodes = codes.data ?? [];
  const effectiveActivity = activity || activityCodes[0] || "";

  // Billing records a recoverable workout cost, so it reuses Workout.RecordCost
  // (held by B4/E1). Only offered when there is unbilled time to bill.
  const unbilledAmount = data?.unbilledAmount ?? 0;
  const canBill = auth.hasPermission("Workout.RecordCost") && unbilledAmount > 0;

  const submit = async () => {
    setError(null);
    try {
      await log.mutateAsync({
        workDate,
        hours: Number(hours),
        activityCode: effectiveActivity,
        note: note || null,
      });
      setHours("");
      setNote("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not log time.");
    }
  };

  const confirmBill = async () => {
    setBillError(null);
    try {
      await bill.mutateAsync();
      setBillOpen(false);
    } catch (e) {
      setBillError(e instanceof Error ? e.message : "Could not bill time.");
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-space-3" data-testid="time-log-section">
        <div className="flex items-center justify-between gap-space-2">
          <h3 className="text-body font-bold m-0">
            Enforcement-administration time (§17.4)
          </h3>
          <div className="flex items-center gap-space-2">
            {canBill && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setBillError(null);
                  setBillOpen(true);
                }}
                data-testid="time-log-bill"
              >
                Bill unbilled time
              </Button>
            )}
            <Badge status="info">
              {formatCurrency(data?.currentHourlyRate ?? 0)}/hr
            </Badge>
          </div>
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-space-3">
            <Stat label="Unbilled hours" value={`${data.unbilledHours.toFixed(2)}h`} />
            <Stat label="Unbilled total" value={formatCurrency(data.unbilledAmount)} />
            <Stat
              label="This month (projected)"
              value={formatCurrency(data.projectedCurrentMonthAmount)}
            />
          </div>
        )}

        {!canLog && data && (
          <p
            className="text-small text-status-error m-0"
            data-testid="time-log-blocked"
          >
            {data.cannotLogReason ??
              "Time logging is not authorized for this case right now."}
          </p>
        )}

        {canLog && (
          <div className="flex flex-wrap items-end gap-space-2">
            <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
              Date
              <input
                type="date"
                className={inputClass}
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
                data-testid="time-log-date"
              />
            </label>
            <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
              Hours
              <input
                type="number"
                step="0.25"
                min="0"
                className={`${inputClass} w-24`}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                data-testid="time-log-hours"
              />
            </label>
            <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
              Activity
              <select
                className={inputClass}
                value={effectiveActivity}
                onChange={(e) => setActivity(e.target.value)}
                data-testid="time-log-activity"
              >
                {activityCodes.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-space-1 text-caption text-gray-mid min-w-40">
              Note
              <input
                type="text"
                className={`${inputClass} w-full`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="time-log-note"
              />
            </label>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void submit()}
              disabled={log.isPending || !hours || Number(hours) <= 0 || !effectiveActivity}
              data-testid="time-log-submit"
            >
              {log.isPending ? "Logging…" : "Log time"}
            </Button>
          </div>
        )}

        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}

        {data && data.entries.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Activity</TH>
                <TH className="text-right">Hours</TH>
                <TH className="text-right">Rate</TH>
                <TH className="text-right">Amount</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {data.entries.map((e) => (
                <TR key={e.id} data-testid={`time-log-row-${e.id}`}>
                  <TD className="whitespace-nowrap">{e.workDate}</TD>
                  <TD>{e.activityCode}</TD>
                  <TD className="text-right tabular-nums">{e.hours.toFixed(2)}</TD>
                  <TD className="text-right tabular-nums">{formatCurrency(e.rateAtLog)}</TD>
                  <TD className="text-right tabular-nums">{formatCurrency(e.amount)}</TD>
                  <TD>
                    {e.rolledUp ? (
                      <Badge status="finalized">Rolled up</Badge>
                    ) : (
                      <Badge status="draft">Unbilled</Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="text-small text-gray-mid m-0">
            No §17.4 time logged for this case yet.
          </p>
        )}
      </div>

      <Modal
        open={billOpen}
        onClose={() => {
          if (!bill.isPending) setBillOpen(false);
        }}
        title="Bill unbilled §17.4 time"
        size="sm"
        footer={
          <div className="flex justify-end gap-space-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setBillOpen(false)}
              disabled={bill.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void confirmBill()}
              disabled={bill.isPending}
              data-testid="time-log-bill-confirm"
            >
              {bill.isPending
                ? "Billing…"
                : `Bill ${formatCurrency(unbilledAmount)}`}
            </Button>
          </div>
        }
      >
        <p className="text-small m-0">
          This converts the case's{" "}
          <strong>{formatCurrency(unbilledAmount)}</strong> of unbilled
          enforcement-administration time into a recoverable cost, routed to
          B3&rsquo;s fee-review queue. Already-billed entries are not affected.
        </p>
        {billError && (
          <p
            className="text-small text-status-error m-0 mt-space-2"
            role="alert"
          >
            {billError}
          </p>
        )}
      </Modal>
    </Card>
  );
}
